import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';

function CalendarView({ userId, onClose, guestMode }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedRecord, setSelectedRecord] = useState(null);

  // 날짜 형식 변환 (2025-10-24 → 25.10.24)
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    return `${year.slice(2)}.${month}.${day}`;
  };

  useEffect(() => {
    loadRecords();
  }, [userId]);

  const loadRecords = async () => {
    setLoading(true);
    try {
      if (guestMode) {
        // 비회원 모드: localStorage에서 로드
        const guestRecords = JSON.parse(localStorage.getItem('guestRecords') || '[]');
        guestRecords.sort((a, b) => new Date(b.date) - new Date(a.date));
        setRecords(guestRecords);
      } else {
        // 회원 모드: Firebase에서 로드
        const recordsRef = collection(db, 'users', userId, 'records');
        const q = query(recordsRef, orderBy('date', 'desc'));
        const querySnapshot = await getDocs(q);

        const recordsData = [];
        querySnapshot.forEach((doc) => {
          recordsData.push({
            id: doc.id,
            ...doc.data()
          });
        });

        setRecords(recordsData);
      }
    } catch (error) {
      console.error('기록 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const getRecordForDate = (dateStr) => {
    return records.find(r => r.date === dateStr);
  };

  const formatDateString = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  return (
    <div className="fade-in" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="slide-up" style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#8B7355'
          }}
        >
          ×
        </button>

        <div style={{
          textAlign: 'center',
          marginBottom: '32px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
          <h2 style={{
            color: '#E8A87C',
            fontSize: '24px',
            marginBottom: '8px'
          }}>
            캘린더
          </h2>
          <p style={{
            color: '#8B7355',
            fontSize: '14px'
          }}>
            기록한 날짜를 한눈에 확인하세요
          </p>
        </div>

        {loading ? (
          <div className="skeleton skeleton-card" style={{ height: '300px' }}></div>
        ) : (
          <>
            {/* 월 네비게이션 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <button
                onClick={prevMonth}
                style={{
                  backgroundColor: 'white',
                  border: '2px solid #E8A87C',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#E8A87C',
                  fontWeight: 'bold'
                }}
              >
                ◀ 이전
              </button>
              <div style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#E8A87C'
              }}>
                {year}년 {month + 1}월
              </div>
              <button
                onClick={nextMonth}
                style={{
                  backgroundColor: 'white',
                  border: '2px solid #E8A87C',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#E8A87C',
                  fontWeight: 'bold'
                }}
              >
                다음 ▶
              </button>
            </div>

            {/* 요일 헤더 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '4px',
              marginBottom: '8px'
            }}>
              {dayNames.map((day, i) => (
                <div key={day} style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  color: i === 0 ? '#E57373' : i === 6 ? '#64B5F6' : '#8B7355',
                  padding: '8px 0'
                }}>
                  {day}
                </div>
              ))}
            </div>

            {/* 달력 날짜 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '4px',
              marginBottom: '20px'
            }}>
              {/* 빈 칸 */}
              {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} style={{ padding: '12px' }}></div>
              ))}

              {/* 날짜 */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = formatDateString(year, month, day);
                const record = getRecordForDate(dateStr);
                const dayOfWeek = (startingDayOfWeek + i) % 7;

                return (
                  <div
                    key={day}
                    onClick={() => record && setSelectedRecord(record)}
                    style={{
                      padding: '12px',
                      textAlign: 'center',
                      fontSize: '14px',
                      borderRadius: '8px',
                      cursor: record ? 'pointer' : 'default',
                      backgroundColor: record ? '#E8A87C' : 'transparent',
                      color: record ? 'white' : dayOfWeek === 0 ? '#E57373' : dayOfWeek === 6 ? '#64B5F6' : '#8B7355',
                      fontWeight: record ? 'bold' : 'normal',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseOver={(e) => {
                      if (record) e.target.style.backgroundColor = '#D4956B';
                    }}
                    onMouseOut={(e) => {
                      if (record) e.target.style.backgroundColor = '#E8A87C';
                    }}
                  >
                    {day}
                    {record && (
                      <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '4px',
                        height: '4px',
                        backgroundColor: 'white',
                        borderRadius: '50%'
                      }}></div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* 선택된 기록 상세 */}
            {selectedRecord && (
              <div style={{
                backgroundColor: '#FAF3E8',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <h3 style={{ color: '#E8A87C', margin: 0 }}>
                    {formatDate(selectedRecord.date)}
                  </h3>
                  <button
                    onClick={() => setSelectedRecord(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '18px',
                      cursor: 'pointer',
                      color: '#8B7355'
                    }}
                  >
                    ×
                  </button>
                </div>
                <div style={{ color: '#8B7355', fontSize: '14px', marginBottom: '8px' }}>
                  <strong>키:</strong> {selectedRecord.height}cm
                </div>
                <div style={{ color: '#8B7355', fontSize: '14px', marginBottom: '8px' }}>
                  <strong>몸무게:</strong> {selectedRecord.weight}kg
                </div>
                {selectedRecord.memo && (
                  <div style={{
                    color: '#8B7355',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    backgroundColor: '#FFF8F0',
                    padding: '8px',
                    borderRadius: '8px',
                    marginTop: '8px'
                  }}>
                    📝 {selectedRecord.memo}
                  </div>
                )}
              </div>
            )}

            <div style={{
              backgroundColor: '#F5E6D3',
              borderRadius: '12px',
              padding: '12px',
              textAlign: 'center',
              fontSize: '12px',
              color: '#8B7355',
              marginBottom: '20px'
            }}>
              💡 색칠된 날짜를 클릭하면 상세 정보를 볼 수 있어요
            </div>
          </>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '16px',
            backgroundColor: '#E8A87C',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#D4956B'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#E8A87C'}
        >
          닫기
        </button>
      </div>
    </div>
  );
}

export default CalendarView;
